import React from 'react';
import './App.css';
import { DragDropContext, DraggableLocation, DropResult } from "react-beautiful-dnd";
import { ArgumentList } from './features/arguments/ArgumentList';
import { useAppDispatch } from './app/hooks';
import { addArgument, ArgumentListKey, deleteArgument, moveArgument } from './features/arguments/argumentsSlice';
import classNames from 'classnames';
import { TrashCan } from './features/arguments/TrashCan';
import { TitleComponent } from './features/title/TitleComponent';
import { LoadButton, loadFile, SaveButton } from './features/save-load/Buttons';
import { setState } from './app/store';
import { ResetButton } from './features/reset-all/ResetButton';
import { Footer } from './features/footer/Footer';
import { SwitchColorButton } from './features/switch-color-theme/SwitchColorButton';
import { SwitchPatternsButton } from './features/patterns/SwitchPatternsButton';
import { Legend } from './features/legend/Legend';
import SwitchLanguageButton from './features/i18n/SwitchLanguageButton';
import { ZenModeButton } from './features/zen-mode/ZenModeButton';
import { simpleContentEditableProps } from './utils/useContentEditableProps';
import { ExportButton } from './features/export/ExportButton';
import { Seesaw } from './features/arguments/Seesaw';

function toArgumentIndex(location: DraggableLocation) {
  return {
    list: location.droppableId as ArgumentListKey,
    index: location.index
  }
}

function App() {
  const dispatch = useAppDispatch();

  const onDragEnd = (e: DropResult) => {
    if (!e.destination) return;
    if (e.destination.droppableId === "trashCan") {
      dispatch(deleteArgument(toArgumentIndex(e.source)))
    }
    else {
      dispatch(moveArgument({
        source: toArgumentIndex(e.source),
        destination: toArgumentIndex(e.destination)
      }));
    }
  }

  return (
    <div
      className="App"
      onDrop={async e => {
        if (e.dataTransfer.items.length !== 1) return;
        e.preventDefault();
        e.stopPropagation();
        const dropped = e.dataTransfer.items[0];
        if (dropped.kind === "file") {
          const file = await dropped.getAsFile()!;
          await loadFile(file);
        }
        else if (dropped.kind === "string") {
          const string = await new Promise<string>(resolve => dropped.getAsString(resolve));
          dispatch(setState(JSON.parse(string)));
        }
      }}
      onDragOver={e => e.preventDefault()}
    >
      <div className="buttons-right">
        <ZenModeButton />
        <SwitchColorButton />
        <SwitchPatternsButton />
        <SwitchLanguageButton />
      </div>

      <TitleComponent field="title" />

      <div className="buttons-left">
        <ResetButton />
        <SaveButton />
        <LoadButton />
        <ExportButton />
        <Legend />
      </div>
      <DragDropContext
        onDragEnd={onDragEnd}>
        <div className="unsorted-list">
          <ArgumentList listKey='unsorted'>
            <PlusButton />
          </ArgumentList >
        </div>

        <Seesaw />

        <div className="bottom-row">
          <TitleComponent field="pro" />
          <TrashCan />
          <TitleComponent field="contra" />
        </div>
      </DragDropContext>

      <Footer />

      <div id="dropdown-container"></div>
    </div >
  );
}

function PlusButton() {
  const [isEditing, setIsEditing] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>();
  const dispatch = useAppDispatch();

  if (isEditing) { ref.current?.focus({ preventScroll: true }); }

  return <div className={classNames("plus-button", { isEditing })}
    onClick={() => {
      setIsEditing(true);
      setTimeout(() => ref.current?.focus());
    }}
    onBlur={() => {
      // Option A: blur = enter
      if (!ref?.current) return;
      dispatch(addArgument(ref.current.innerText));
      ref.current.innerText = "";
      setIsEditing(false);

      // Option B: only disable editing when empty
      // if (ref.current?.innerText.trim().length === 0) {
      //   setIsEditing(false);
      //   ref.current.innerText = " ";
      // }
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter") {
        if (!ref?.current) return;
        dispatch(addArgument(ref.current.innerText));
        ref.current.innerText = "";
        e.preventDefault();
      }
    }}
  >
    {isEditing ?
      <div
        {...simpleContentEditableProps}
        ref={x => {
          if (x) {
            x.focus();
            ref.current = x;
          }
        }}
      ></div> :
      <div>+</div>}
  </div>
}

export default App;
